// An ACF Link field happily stores a bare fragment ("#some-anchor") and
// esc_url() passes it through untouched, so the markup was never the problem —
// what the fragment needs is someone to act on it:
//
//  * swup intercepts every in-page link and, when a hash has no matching
//    element, falls through to its scroll-reset and throws you to the top of
//    the page — so an anchor that isn't a plain id looks broken;
//  * swup's own anchor scrolling is an instant scrollIntoView(), which parks
//    the target underneath the fixed header;
//  * some fragments aren't places at all. Pointing a CTA at the floating
//    credit estimator's toggle should open the estimator, not scroll to the
//    corner of the screen its button happens to sit in.

// Fragments matching one of these are clicked instead of scrolled to.
const ACTIVATABLE = 'button, summary, [role="button"]';

// An element can also delegate: a fragment landing on it clicks whatever this
// selector points at instead. The estimator's wrapper is the case in point —
// #credit-estimator-widget is the id an editor reaches for, but it's a
// position:fixed shell, so scrolling to it does nothing at all.
const DELEGATE_ATTR = "data-hash-activate";

// Breathing room between the fixed header and the target.
const GUTTER = 24;

function headerOffset() {
    const header = document.querySelector("#wrapper-navbar");
    if (!header) return GUTTER;

    // Only a pinned header actually covers the target; if it scrolls away with
    // the page there is nothing to compensate for.
    const { position } = window.getComputedStyle(header);
    if (position !== "fixed" && position !== "sticky") return GUTTER;

    return header.getBoundingClientRect().height + GUTTER;
}

/**
 * Find what a fragment points at.
 *
 * `data-hash-id` is an alias, for elements that already carry an id used by
 * something else (scripts, aria-controls) but should still be reachable under
 * a friendlier name from an ACF Link field.
 */
export function resolveHash(hash) {
    const id = decodeURIComponent(String(hash ?? "").replace(/^#/, ""));
    if (!id) return null;

    // "#top" is the one fragment with no element behind it by convention.
    if (id === "top") return document.body;

    const escaped = CSS.escape(id);

    return (
        document.getElementById(id) ||
        document.querySelector(`[data-hash-id="${escaped}"]`) ||
        document.querySelector(`a[name="${escaped}"]`)
    );
}

/**
 * Act on a fragment. Returns false when nothing matched, so callers can decide
 * whether to fall back to their own behaviour.
 */
export function activateHash(hash, { smooth = true } = {}) {
    const target = resolveHash(hash);
    if (!target) return false;

    const delegate = target.getAttribute(DELEGATE_ATTR);
    if (delegate) {
        const handoff = document.querySelector(delegate);
        if (handoff) {
            handoff.click();
            return true;
        }
    }

    if (target.matches(ACTIVATABLE)) {
        target.click();
        return true;
    }

    const top = Math.max(
        0,
        target.getBoundingClientRect().top + window.scrollY - headerOffset()
    );

    // style.scss already sets scroll-behavior: smooth on html/body, so the
    // smooth case can just hand the browser a position — and the instant case
    // has to ask for "instant" explicitly to opt back out of it.
    window.scrollTo({ top, behavior: smooth ? "smooth" : "instant" });

    return true;
}

/**
 * Landing on a URL that already carries a fragment, plus a click handler for
 * the links swup leaves alone — swup routes the ones it claims through its own
 * scroll:anchor hook instead (see initPageTransitions).
 */
export function initHashLinks() {
    document.addEventListener("click", (event) => {
        if (event.defaultPrevented || event.button !== 0) return;
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

        const link = event.target.closest?.("a[href]");
        if (!link) return;

        // Swup skips anything that opens a new window, so those never reach its
        // hook — and a fragment pointing at the page you're already on is exactly
        // the sort of link an editor ticks "open in a new tab" on out of habit.
        // Handling it here is also the only way that tick stops breaking the link.
        const swupLive = document.documentElement.classList.contains("swup-enabled");
        const opensNewWindow = link.matches('[download], [target="_blank"]');
        if (swupLive && !opensNewWindow) return;

        let url;
        try {
            url = new URL(link.href, window.location.href);
        } catch (e) {
            return;
        }

        // Anything pointing off this page is a real navigation — leave it be.
        if (!url.hash || url.origin !== window.location.origin) return;
        if (url.pathname !== window.location.pathname || url.search !== window.location.search) return;

        if (!activateHash(url.hash)) return;

        event.preventDefault();
        window.history.replaceState(window.history.state, "", url.hash);
    });

    if (window.location.hash) {
        // The curtain is still covering the screen on first load, so jump
        // rather than animate — and wait a frame for layout to settle, since
        // the browser's own hash jump has already happened by now and the
        // fade-up/split-text setup can still move things around.
        requestAnimationFrame(() => activateHash(window.location.hash, { smooth: false }));
    }
}
